<template>
  <div class="table-widget">
    <!-- Asset Lifecycle Table -->
    <div v-if="widget.templateId === 'metric_asset_lifecycle'" class="asset-lifecycle-table">
      <v-data-table
        :headers="assetHeaders"
        :items="assetData"
        :items-per-page="10"
        :loading="loading"
        class="elevation-0"
        dense
        hide-default-footer
      >
        <template v-slot:[`item.status`]="{ item }">
          <v-chip
            :color="getStatusColor(item.status)"
            text-color="white"
            small
          >
            {{ item.status }}
          </v-chip>
        </template>
        <template v-slot:[`item.warrantyStatus`]="{ item }">
          <v-chip
            :color="getWarrantyColor(item.warrantyStatus)"
            text-color="white"
            small
          >
            {{ item.warrantyStatus }}
          </v-chip>
        </template>
        <template v-slot:[`item.batteryLevel`]="{ item }">
          <div class="d-flex align-center">
            <v-progress-linear
              :value="item.batteryLevel"
              :color="getBatteryColor(item.batteryLevel)"
              height="6"
              rounded
              class="mr-2"
              style="min-width: 60px;"
            ></v-progress-linear>
            <span class="text-caption">{{ item.batteryLevel }}%</span>
          </div>
        </template>
        <template v-slot:[`item.nextMaintenance`]="{ item }">
          <span :class="getMaintenanceClass(item.nextMaintenance)">
            {{ formatDate(item.nextMaintenance) }}
          </span>
        </template>
      </v-data-table>
    </div>
    
    <!-- Maintenance Schedule Table -->
    <div v-else-if="widget.templateId === 'metric_maintenance_schedule'" class="maintenance-schedule-table">
      <v-data-table
        :headers="maintenanceHeaders"
        :items="maintenanceData"
        :items-per-page="10"
        :loading="loading"
        class="elevation-0"
        dense
        hide-default-footer
      >
        <template v-slot:[`item.type`]="{ item }">
          <v-chip
            :color="getTypeColor(item.type)"
            text-color="white"
            small
          >
            <v-icon left small>{{ getTypeIcon(item.type) }}</v-icon>
            {{ item.type }}
          </v-chip>
        </template>
        <template v-slot:[`item.priority`]="{ item }">
          <v-chip
            :color="getPriorityColor(item.priority)"
            text-color="white"
            small
          >
            {{ item.priority }}
          </v-chip>
        </template>
        <template v-slot:[`item.status`]="{ item }">
          <v-chip
            :color="getMaintenanceStatusColor(item.status)"
            text-color="white"
            small
          >
            {{ item.status }}
          </v-chip>
        </template>
        <template v-slot:[`item.scheduledDate`]="{ item }">
          <span :class="getScheduleDateClass(item.scheduledDate)">
            {{ formatDateTime(item.scheduledDate) }}
          </span>
        </template>
      </v-data-table>
    </div>
    
    <!-- Generic Table -->
    <div v-else class="generic-table">
      <v-data-table
        :headers="genericHeaders"
        :items="genericData"
        :items-per-page="5"
        class="elevation-0"
        dense
        hide-default-footer
      >
      </v-data-table>
    </div>
  </div>
</template>

<script>
import { getLocationsByUser, getWorkOrders, getInspections } from '@/services/firebase-helpers';

export default {
  name: 'TableWidget',
  props: {
    widget: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      assetData: [],
      maintenanceData: [],
      assetHeaders: [
        { text: 'Device ID', value: 'deviceId', width: '15%' },
        { text: 'Location', value: 'location', width: '15%' },
        { text: 'Age', value: 'age', width: '10%' },
        { text: 'Status', value: 'status', width: '12%' },
        { text: 'Battery', value: 'batteryLevel', width: '15%' },
        { text: 'Warranty', value: 'warrantyStatus', width: '12%' },
        { text: 'Next Maintenance', value: 'nextMaintenance', width: '21%' }
      ],
      maintenanceHeaders: [
        { text: 'Type', value: 'type', width: '15%' },
        { text: 'Title', value: 'title', width: '25%' },
        { text: 'Device', value: 'deviceId', width: '15%' },
        { text: 'Priority', value: 'priority', width: '10%' },
        { text: 'Status', value: 'status', width: '12%' },
        { text: 'Scheduled', value: 'scheduledDate', width: '18%' },
        { text: 'Assignee', value: 'assignee', width: '15%' }
      ],
      genericHeaders: [
        { text: 'Name', value: 'name' },
        { text: 'Value', value: 'value' },
        { text: 'Status', value: 'status' }
      ],
      genericData: [
        { name: 'Sample Item 1', value: '123', status: 'Active' },
        { name: 'Sample Item 2', value: '456', status: 'Inactive' },
        { name: 'Sample Item 3', value: '789', status: 'Active' }
      ]
    }
  },
  methods: {
    async loadAssetLifecycleData() {
      this.loading = true;
      try {
        const userId = this.$store.state.auth?.uid || localStorage.getItem('uid');
        if (!userId) {
          this.assetData = [];
          return;
        }

        // Get user's locations and devices
        const locations = await getLocationsByUser(userId);
        if (!locations || locations.length === 0) {
          this.assetData = [];
          return;
        }

        // Process each location's devices
        const assets = [];
        locations.forEach(location => {
          if (location.devices && location.devices.length > 0) {
            location.devices.forEach(deviceId => {
              // Generate realistic asset lifecycle data
              const installDate = this.generateInstallDate();
              const age = this.calculateAge(installDate);
              const warrantyYears = Math.random() > 0.7 ? 3 : 2; // 30% get 3-year warranty
              const warrantyExpiry = new Date(installDate);
              warrantyExpiry.setFullYear(warrantyExpiry.getFullYear() + warrantyYears);
              
              const asset = {
                deviceId: deviceId,
                location: location.name || `Location ${location.key}`,
                installDate: installDate,
                age: age,
                status: this.generateDeviceStatus(),
                batteryLevel: Math.floor(Math.random() * 40) + 60, // 60-100%
                warrantyStatus: this.getWarrantyStatus(warrantyExpiry),
                warrantyExpiry: warrantyExpiry,
                nextMaintenance: this.generateNextMaintenance(),
                firmwareVersion: this.generateFirmwareVersion(),
                lastSeen: this.generateLastSeen()
              };
              
              assets.push(asset);
            });
          }
        });

        // Sort by next maintenance date (soonest first)
        assets.sort((a, b) => new Date(a.nextMaintenance) - new Date(b.nextMaintenance));
        
        this.assetData = assets;

      } catch (error) {
        console.error('Error loading asset lifecycle data:', error);
        // Fallback mock data
        this.assetData = [
          {
            deviceId: 'ximu3-001',
            location: 'Main Facility',
            age: '2.3 years',
            status: 'Active',
            batteryLevel: 85,
            warrantyStatus: 'Valid',
            nextMaintenance: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000)
          },
          {
            deviceId: 'ximu3-002',
            location: 'Warehouse A',
            age: '1.8 years',
            status: 'Warning',
            batteryLevel: 72,
            warrantyStatus: 'Expiring',
            nextMaintenance: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
          }
        ];
      } finally {
        this.loading = false;
      }
    },
    async loadMaintenanceScheduleData() {
      this.loading = true;
      try {
        // Get work orders and inspections from Firebase
        const [workOrders, inspections] = await Promise.all([
          getWorkOrders(),
          getInspections()
        ]);

        console.log('Loaded work orders:', workOrders);
        console.log('Loaded inspections:', inspections);

        const maintenanceItems = [];

        // Process work orders
        if (workOrders && workOrders.length > 0) {
          workOrders.forEach(workOrder => {
            // Only include future or current work orders
            const scheduledDate = new Date(workOrder.scheduledDate || workOrder.createdAt);
            const now = new Date();
            
            if (workOrder.status !== 'Completed' && workOrder.status !== 'Cancelled') {
              maintenanceItems.push({
                id: workOrder.id || workOrder.key,
                type: 'Work Order',
                title: workOrder.title || `WO: ${workOrder.workOrderNumber || 'N/A'}`,
                deviceId: workOrder.deviceId || 'N/A',
                priority: workOrder.priority || 'Medium',
                status: workOrder.status || 'Open',
                scheduledDate: scheduledDate,
                assignee: workOrder.assignedTo || 'Unassigned',
                description: workOrder.description || '',
                estimatedHours: workOrder.estimatedHours || 0
              });
            }
          });
        }

        // Process inspections
        if (inspections && inspections.length > 0) {
          inspections.forEach(inspection => {
            // Only include future or current inspections
            const scheduledDate = new Date(inspection.scheduledDate || inspection.createdAt);
            const now = new Date();
            
            if (inspection.status !== 'Completed' && inspection.status !== 'Cancelled') {
              maintenanceItems.push({
                id: inspection.id || inspection.key,
                type: 'Inspection',
                title: inspection.title || `Inspection: ${inspection.inspectionNumber || 'N/A'}`,
                deviceId: inspection.deviceId || 'N/A',
                priority: inspection.priority || 'Medium',
                status: inspection.status || 'Scheduled',
                scheduledDate: scheduledDate,
                assignee: inspection.inspector || 'Unassigned',
                description: inspection.description || '',
                estimatedHours: inspection.estimatedDuration || 0
              });
            }
          });
        }

        // If no real data, generate some mock maintenance items
        if (maintenanceItems.length === 0) {
          this.generateMockMaintenanceData();
          return;
        }

        // Sort by scheduled date (soonest first)
        maintenanceItems.sort((a, b) => new Date(a.scheduledDate) - new Date(b.scheduledDate));
        
        // Limit to next 10 items
        this.maintenanceData = maintenanceItems.slice(0, 10);

      } catch (error) {
        console.error('Error loading maintenance schedule data:', error);
        this.generateMockMaintenanceData();
      } finally {
        this.loading = false;
      }
    },
    generateMockMaintenanceData() {
      const mockItems = [];
      const now = new Date();
      
      // Generate some upcoming work orders
      for (let i = 0; i < 5; i++) {
        const scheduledDate = new Date(now.getTime() + (i + 1) * 7 * 24 * 60 * 60 * 1000); // Weekly intervals
        mockItems.push({
          id: `mock_wo_${i}`,
          type: 'Work Order',
          title: `Critical Fastener Maintenance ${i + 1}`,
          deviceId: `ximu3-00${i + 1}`,
          priority: i < 2 ? 'High' : 'Medium',
          status: 'Open',
          scheduledDate: scheduledDate,
          assignee: i % 2 === 0 ? 'John Smith' : 'Jane Doe',
          description: 'Routine maintenance of critical fasteners',
          estimatedHours: 2
        });
      }

      // Generate some upcoming inspections
      for (let i = 0; i < 3; i++) {
        const scheduledDate = new Date(now.getTime() + (i + 1) * 10 * 24 * 60 * 60 * 1000); // 10-day intervals
        mockItems.push({
          id: `mock_insp_${i}`,
          type: 'Inspection',
          title: `Routine Safety Inspection ${i + 1}`,
          deviceId: `ximu3-00${i + 3}`,
          priority: 'Medium',
          status: 'Scheduled',
          scheduledDate: scheduledDate,
          assignee: 'Safety Inspector',
          description: 'Scheduled safety inspection',
          estimatedHours: 1.5
        });
      }

      // Sort by scheduled date
      mockItems.sort((a, b) => new Date(a.scheduledDate) - new Date(b.scheduledDate));
      this.maintenanceData = mockItems;
    },
    generateInstallDate() {
      // Generate install date between 6 months and 4 years ago
      const now = new Date();
      const minMonthsAgo = 6;
      const maxMonthsAgo = 48;
      const monthsAgo = Math.floor(Math.random() * (maxMonthsAgo - minMonthsAgo)) + minMonthsAgo;
      
      const installDate = new Date(now);
      installDate.setMonth(installDate.getMonth() - monthsAgo);
      return installDate;
    },
    calculateAge(installDate) {
      const now = new Date();
      const diffTime = Math.abs(now - installDate);
      const diffYears = diffTime / (1000 * 60 * 60 * 24 * 365.25);
      
      if (diffYears < 1) {
        const months = Math.floor(diffYears * 12);
        return `${months} months`;
      } else {
        return `${diffYears.toFixed(1)} years`;
      }
    },
    generateDeviceStatus() {
      const statuses = ['Active', 'Warning', 'Critical', 'Maintenance'];
      const weights = [0.7, 0.2, 0.05, 0.05]; // 70% active, 20% warning, etc.
      
      const random = Math.random();
      let cumulative = 0;
      
      for (let i = 0; i < weights.length; i++) {
        cumulative += weights[i];
        if (random <= cumulative) {
          return statuses[i];
        }
      }
      
      return 'Active';
    },
    getWarrantyStatus(warrantyExpiry) {
      const now = new Date();
      const daysUntilExpiry = (warrantyExpiry - now) / (1000 * 60 * 60 * 24);
      
      if (daysUntilExpiry < 0) return 'Expired';
      if (daysUntilExpiry < 90) return 'Expiring';
      return 'Valid';
    },
    generateNextMaintenance() {
      // Generate maintenance date between 1 week and 6 months from now
      const now = new Date();
      const daysFromNow = Math.floor(Math.random() * 180) + 7; // 7-187 days
      
      const maintenanceDate = new Date(now);
      maintenanceDate.setDate(maintenanceDate.getDate() + daysFromNow);
      return maintenanceDate;
    },
    generateFirmwareVersion() {
      const major = Math.floor(Math.random() * 3) + 1; // 1-3
      const minor = Math.floor(Math.random() * 10); // 0-9
      const patch = Math.floor(Math.random() * 20); // 0-19
      return `${major}.${minor}.${patch}`;
    },
    generateLastSeen() {
      // Generate last seen between 1 minute and 24 hours ago
      const now = new Date();
      const minutesAgo = Math.floor(Math.random() * 1440) + 1; // 1-1440 minutes
      
      const lastSeen = new Date(now);
      lastSeen.setMinutes(lastSeen.getMinutes() - minutesAgo);
      return lastSeen;
    },
    getStatusColor(status) {
      const colorMap = {
        'Active': 'green',
        'Warning': 'orange',
        'Critical': 'red',
        'Maintenance': 'blue'
      };
      return colorMap[status] || 'grey';
    },
    getWarrantyColor(status) {
      const colorMap = {
        'Valid': 'green',
        'Expiring': 'orange',
        'Expired': 'red'
      };
      return colorMap[status] || 'grey';
    },
    getBatteryColor(level) {
      if (level > 50) return 'green';
      if (level > 20) return 'orange';
      return 'red';
    },
    getMaintenanceClass(date) {
      const now = new Date();
      const daysUntil = (new Date(date) - now) / (1000 * 60 * 60 * 24);
      
      if (daysUntil < 7) return 'red--text';
      if (daysUntil < 30) return 'orange--text';
      return '';
    },
    getTypeColor(type) {
      const colorMap = {
        'Work Order': 'blue',
        'Inspection': 'green'
      };
      return colorMap[type] || 'grey';
    },
    getTypeIcon(type) {
      const iconMap = {
        'Work Order': 'mdi-wrench',
        'Inspection': 'mdi-clipboard-check'
      };
      return iconMap[type] || 'mdi-calendar';
    },
    getPriorityColor(priority) {
      const colorMap = {
        'High': 'red',
        'Medium': 'orange',
        'Low': 'green'
      };
      return colorMap[priority] || 'grey';
    },
    getMaintenanceStatusColor(status) {
      const colorMap = {
        'Open': 'blue',
        'In Progress': 'orange',
        'Scheduled': 'green',
        'Completed': 'grey',
        'Cancelled': 'red'
      };
      return colorMap[status] || 'grey';
    },
    getScheduleDateClass(date) {
      const now = new Date();
      const daysUntil = (new Date(date) - now) / (1000 * 60 * 60 * 24);
      
      if (daysUntil < 1) return 'red--text font-weight-bold';
      if (daysUntil < 7) return 'orange--text font-weight-medium';
      return '';
    },
    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    },
    formatDateTime(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  },
  mounted() {
    if (this.widget.templateId === 'metric_asset_lifecycle') {
      this.loadAssetLifecycleData();
      
      // Refresh every 5 minutes
      this.dataInterval = setInterval(() => {
        this.loadAssetLifecycleData();
      }, 300000);
    } else if (this.widget.templateId === 'metric_maintenance_schedule') {
      this.loadMaintenanceScheduleData();
      
      // Refresh every 2 minutes
      this.dataInterval = setInterval(() => {
        this.loadMaintenanceScheduleData();
      }, 120000);
    }
  },
  beforeDestroy() {
    if (this.dataInterval) {
      clearInterval(this.dataInterval);
    }
  }
}
</script>

<style scoped>
.table-widget {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.asset-lifecycle-table,
.maintenance-schedule-table,
.generic-table {
  height: 100%;
  overflow: auto;
}

.v-data-table {
  background: transparent !important;
}

.v-data-table >>> .v-data-table__wrapper {
  overflow-x: auto;
  max-height: 100%;
}

.v-data-table >>> tbody tr {
  cursor: default;
}

.v-data-table >>> tbody tr:hover {
  background-color: rgba(0,0,0,0.03);
}

.v-data-table >>> .v-data-table-header {
  background-color: #f5f5f5;
}

.v-data-table >>> .v-data-table-header th {
  font-weight: 600 !important;
  color: #424242 !important;
}

/* Custom scrollbar */
.asset-lifecycle-table::-webkit-scrollbar,
.maintenance-schedule-table::-webkit-scrollbar,
.generic-table::-webkit-scrollbar {
  width: 6px;
}

.asset-lifecycle-table::-webkit-scrollbar-track,
.maintenance-schedule-table::-webkit-scrollbar-track,
.generic-table::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.asset-lifecycle-table::-webkit-scrollbar-thumb,
.maintenance-schedule-table::-webkit-scrollbar-thumb,
.generic-table::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.asset-lifecycle-table::-webkit-scrollbar-thumb:hover,
.maintenance-schedule-table::-webkit-scrollbar-thumb:hover,
.generic-table::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}

@media (max-width: 960px) {
  .v-data-table >>> .v-data-table__wrapper {
    overflow-x: scroll;
  }
}
</style> 